import React from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { useHelpTextId } from '../../hooks/useHelpTextId'
import { BaseCellProps } from '../BaseCell/BaseCell'
import { Box } from '../Box/Box'
import { cellHoverStyles } from '../InteractiveCell/InteractiveCell'
import { Flex, FullWidthFlex } from '../layout/Flex'
import ArcadeRadioButton, { ArcadeRadioButtonProps } from '../RadioButton/ArcadeRadioButton'

const StyledBox = styled(Box)`
  flex: 1;

  @media (hover: hover) {
    &:has(label:hover) {
      label {
        border-radius: ${({ theme }) => theme.border.radius.circle};
        margin: -${({ theme }) => theme.spacing.m};
        padding: ${({ theme }) => theme.spacing.m};
      }
    }
  }

  /* unchecked */
  background: ${({ theme }) => theme.arcadeColors.component.inputBackgroundDefault};
  border: 0.125rem solid ${({ theme }) => theme.arcadeColors.component.inputBorderDefault};

  /* checked */
  &:has(input:checked) {
    border: 0.125rem solid ${({ theme }) => theme.arcadeColors.component.inputBorderSelected};
  }

  /* active & not disabled */
  &:has(input:active):not(:has(input:disabled)) {
    label {
      border-radius: ${({ theme }) => theme.border.radius.circle};
      margin: -${({ theme }) => theme.spacing.m};
      padding: ${({ theme }) => theme.spacing.m};
      background: ${({ theme }) => theme.arcadeColors.component.inputBackgroundPressed};
    }
  }

  /* focused */
  ${({ theme }) => theme.selectors.keyboard} &:has(input:focus) {
    border: 0.125rem solid ${({ theme }) => theme.arcadeColors.component.inputBorderFocus};
  }

  /* disabled */
  &:has(input:disabled) {
    border: 0.125rem solid ${({ theme }) => theme.arcadeColors.component.radioInactiveBorderDisabled};
  }
`

const StyledLabel = styled.label<{ disabled: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: block;
  position: relative;

  ${({ theme, disabled }) => cellHoverStyles(theme, disabled)}

  /* focused */
  ${({ theme }) => theme.selectors.keyboard} &:has(input:focus) {
    border-radius: ${({ theme }) => theme.border.radius.circle};
    outline: 0.125rem solid ${({ theme }) => theme.arcadeColors.component.inputBorderFocus};
    margin: -${({ theme }) => theme.spacing.m};
    padding: ${({ theme }) => theme.spacing.m};
  }
`

export interface RadioImageProps extends ArcadeRadioButtonProps, Pick<BaseCellProps, 'label'> {
  cellProps: Omit<BaseCellProps, 'accessory' | 'disabled' | 'label'> & { body: React.ReactNode }
}

const RadioImage = React.forwardRef<HTMLDivElement, RadioImageProps>((props, ref) => {
  const theme = useTheme()

  const { label, disabled = false, checked, cellProps, ...restProps } = props
  const helpTextId = useHelpTextId(cellProps)

  return (
    <StyledBox padding="m" borderRadius={theme.border.radius.circle}>
      <StyledLabel disabled={disabled}>
        <Flex ref={ref}>
          <FullWidthFlex>{cellProps?.body}</FullWidthFlex>
          <ArcadeRadioButton
            checked={checked}
            disabled={disabled}
            data-testid="visible-radio"
            helpTextId={helpTextId}
            isSegmentedControl={true}
            aria-label={label}
            {...restProps}
          />
        </Flex>
      </StyledLabel>
    </StyledBox>
  )
})

export default RadioImage
